import React from 'react';
import { Search, ArrowUpDown } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export type SortField = 'client' | 'city' | 'date';

interface AssignmentFiltersProps {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    sortBy: SortField;
    setSortBy: (sort: SortField) => void;
    selectedMonth: string;
    setSelectedMonth: (month: string) => void;
    selectedDate: string;
    setSelectedDate: (date: string) => void;
    selectedStatus: string;
    setSelectedStatus: (status: string) => void;
    selectedYear: number;
    setSelectedYear: (year: number) => void;
    onExport: () => void;
    viewMode: 'list' | 'kanban';
    setViewMode: (mode: 'list' | 'kanban') => void;
}

export const AssignmentFilters: React.FC<AssignmentFiltersProps> = ({
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    selectedMonth,
    setSelectedMonth,
    selectedDate,
    setSelectedDate,
    selectedStatus,
    setSelectedStatus,
    selectedYear,
    setSelectedYear,
    onExport,
    viewMode,
    setViewMode
}) => {
    return (
        <div className="glass-panel p-4 space-y-4">
            {/* Search and Sort */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Rechercher par client, ville, technicien..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="glass-input pl-10 w-full"
                    />
                </div>
                <div className="flex gap-2">
                    <span className="text-sm text-gray-400 flex items-center gap-2">
                        <ArrowUpDown size={16} />
                        Trier par :
                    </span>
                    {[
                        { value: 'client', label: 'Nom' },
                        { value: 'city', label: 'Ville' },
                        { value: 'date', label: 'Date' },
                    ].map(sort => (
                        <button
                            key={sort.value}
                            onClick={() => setSortBy(sort.value as SortField)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${sortBy === sort.value
                                ? 'bg-accent/20 text-accent border border-accent/50'
                                : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                                }`}
                        >
                            {sort.label}
                        </button>
                    ))}
                    <button
                        onClick={onExport}
                        className="px-3 py-2 rounded-lg text-xs font-medium transition-all bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 flex items-center gap-2"
                        title="Exporter en CSV"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
                        Export CSV
                    </button>

                    <div className="h-6 w-px bg-white/10 mx-2" />

                    <div className="flex bg-white/5 p-1 rounded-lg border border-white/10">
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-accent text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                            title="Vue Liste"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                        </button>
                        <button
                            onClick={() => setViewMode('kanban')}
                            className={`p-1.5 rounded-md transition-all ${viewMode === 'kanban' ? 'bg-accent text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                            title="Vue Kanban"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="3" x2="9" y2="21"></line></svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Advanced Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-white/5">
                {/* Year Filter */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-gray-500 font-medium">Année</label>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="glass-input w-full"
                    >
                        {Array.from({ length: 5 }).map((_, i) => {
                            const year = new Date().getFullYear() - 1 + i; // Current year - 1 to +3
                            return (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            );
                        })}
                    </select>
                </div>

                {/* Month Filter */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-gray-500 font-medium">Mois</label>
                    <select
                        value={selectedMonth}
                        onChange={(e) => {
                            setSelectedMonth(e.target.value);
                            if (e.target.value !== 'all') setSelectedDate(''); // Clear specific date if month selected
                        }}
                        className="glass-input w-full"
                    >
                        <option value="all">Tous les mois</option>
                        {Array.from({ length: 12 }).map((_, i) => {
                            const date = new Date(selectedYear, i, 1);
                            return (
                                <option key={i} value={i.toString()}>
                                    {format(date, 'MMMM yyyy', { locale: fr })}
                                </option>
                            );
                        })}
                    </select>
                </div>

                {/* Date Filter */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-gray-500 font-medium">Date spécifique</label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => {
                            setSelectedDate(e.target.value);
                            if (e.target.value) setSelectedMonth('all'); // Clear month filter if specific date
                        }}
                        className="glass-input w-full"
                    />
                </div>

                {/* Status Filter */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-gray-500 font-medium">Statut</label>
                    <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="glass-input w-full"
                    >
                        <option value="all">Tous les statuts</option>
                        <option value="pending">En attente</option>
                        <option value="in-progress">En cours</option>
                        <option value="completed-by-tech">Terminé</option>
                        <option value="closed">Clôturé</option>
                    </select>
                </div>
            </div>
        </div >
    );
};
